import { FC, useCallback } from "react";

import {
  switchableColorTitles,
  switchableSimilarityTitles,
} from "../../constants";

import { aiTitleToEmbedder } from "../../details/modals/helpers";
import { AIMethods, EmbedderConfig, EmbedderProperty } from "../../types";
import { SwitchableAIField } from "./SwitchableAIField";

import { BooleanVoidFunc, StateAction } from "../../../helpers/functions/types";
import { NamedSwitch } from "../../../ui/layout/molecules/NamedSwitch";
import { Restricted } from "../../../components/Restricted";

import { detailsBlockStyles } from "../../../ui/layout/detailedPages/detailsBlockStyles";
import { netTitleToSubtitle } from "../../../helpers/titles/netTitleToSubtitle";
import {
  DETAILS_SECTION_STYLES,
  SUB_HEADER_STYLES,
} from "../../../ui/layout/detailedPages/styles";

const similarityTitles = Object.keys(switchableSimilarityTitles);
const colorTitles = Object.keys(switchableColorTitles);

export const AiMethodsEditBlock: FC<{
  embeddersConfig: EmbedderConfig[];
  setEmbeddersConfig: StateAction<EmbedderConfig[]>;
  useBarraquito: boolean;
  setUseBarraquito: BooleanVoidFunc;
  showInSlideOver?: boolean;
}> = ({
  embeddersConfig,
  setEmbeddersConfig,
  useBarraquito,
  setUseBarraquito,
  showInSlideOver,
}) => {
  const {
    detailsGroupStyles,
    detailsSwitchableStyles,
    detailsRowStyles,
    customWrapperStyles,
  } = detailsBlockStyles(showInSlideOver);

  const setNewEmbedderConfig = useCallback(
    (title: AIMethods, property: EmbedderProperty) => (value: boolean) => {
      const embedder = aiTitleToEmbedder(title, embeddersConfig);
      if (embedder) {
        const updatedElement = { ...embedder };
        updatedElement[property] = value;
        setEmbeddersConfig((embeddersConfig) =>
          embeddersConfig.map((item) =>
            item.aiTitle === title ? updatedElement : item,
          ),
        );
      }
    },
    [embeddersConfig],
  );

  const renderSection = (
    sectionTitle: string,
    sectionItemTitles: string[],
    customMethods?: JSX.Element,
  ) => {
    return (
      <section className={DETAILS_SECTION_STYLES}>
        <h4 className={SUB_HEADER_STYLES}>{sectionTitle}</h4>
        <div className={detailsGroupStyles}>
          {sectionItemTitles.map((title) => {
            const embedder = aiTitleToEmbedder(
              title as AIMethods,
              embeddersConfig,
            );
            return (
              <SwitchableAIField
                key={title}
                embedder={embedder}
                onChange={setNewEmbedderConfig}
                customGroupStyles={detailsSwitchableStyles}
                customWrapperStyles={customWrapperStyles}
              />
            );
          })}
          {!!customMethods && customMethods}
        </div>
      </section>
    );
  };

  const Barraquito = (
    <div className={detailsRowStyles}>
      <NamedSwitch
        title="Barraquito Net"
        subtitle={netTitleToSubtitle("Barraquito")}
        value={useBarraquito}
        onChange={setUseBarraquito}
      />
    </div>
  );

  return (
    <Restricted permissions="site:advanced_config:view">
      {renderSection("Similarity", similarityTitles)}
      {renderSection("Color", colorTitles, Barraquito)}
    </Restricted>
  );
};
